import {
  ICartControllerApi,
  CartStoreProps,
  SettingsOverrides,
  VeloInputs,
  ExperimentProps,
} from '../../types/app.types';
import {CartService} from '../services/CartService';
import {ProductsService} from '../services/ProductsService';
import {BIService} from '../services/BIService';
import {FedopsInteractions} from '../../common/constants';
import {StyleSettingsService} from '../services/StyleSettingsService';
import {SiteStore} from '@wix/wixstores-client-storefront-sdk';
import {SPECS} from '../specs';
import {MinimumOrderAmountService} from '../services/MinimumOrderAmountService';
import {calcTopThreeViolations} from '../utils/violations.utils';
import {LineItemModel} from '../models/LineItem.model';
import {ControllerParams} from '@wix/yoshi-flow-editor';

export class CartStore {
  private readonly biService: BIService;
  private readonly cartService: CartService;
  private readonly productsService: ProductsService;
  private readonly controllerApi: ICartControllerApi;
  private readonly flowAPI: ControllerParams['flowAPI'];
  private readonly styleSettingsService: StyleSettingsService;
  private readonly minimumOrderAmountService: MinimumOrderAmountService;
  private screenReaderMessage: string;
  private settingsOverrides: SettingsOverrides;
  private loadingItems: number[] = [];

  constructor(
    flowAPI: ControllerParams['flowAPI'],
    controllerApi: ICartControllerApi,
    private readonly siteStore: SiteStore,
    {
      biService,
      cartService,
      productsService,
      styleSettingsService,
      minimumOrderAmountService,
    }: {
      biService: BIService;
      cartService: CartService;
      productsService: ProductsService;
      styleSettingsService: StyleSettingsService;
      minimumOrderAmountService: MinimumOrderAmountService;
    },
    veloInputs: VeloInputs
  ) {
    this.setOverrides(veloInputs);
    this.controllerApi = controllerApi;
    this.flowAPI = flowAPI;
    this.biService = biService;
    this.cartService = cartService;
    this.productsService = productsService;
    this.styleSettingsService = styleSettingsService;
    this.minimumOrderAmountService = minimumOrderAmountService;
  }

  public clearLoadingItems = () => {
    this.loadingItems = [];
  };

  private readonly clearError = async () => {
    this.cartService.setHasErrorState(false);
    await this.controllerApi.updateComponent(false);
  };

  private readonly updateItemQuantity = async (cartItemId: number, quantity: number, productId: string) => {
    this.loadingItems.push(cartItemId);
    await this.controllerApi.updateComponent(true);
    try {
      return await this.controllerApi.reportFedops(FedopsInteractions.UpdateQuantity, () =>
        this.cartService.updateItemQuantity(cartItemId, quantity, productId)
      );
    } catch (e) {
      await this.catchServerError(e as Error);
    }
  };

  private readonly removeItemFromCart = async (lineItem: LineItemModel) => {
    this.loadingItems.push(lineItem.id);
    await this.controllerApi.updateComponent(true);
    this.screenReaderMessage = this.controllerApi.t('cart.sr_item_was_removed', {item_name: lineItem.productName});
    try {
      return await this.controllerApi.reportFedops(FedopsInteractions.RemoveItem, () =>
        this.cartService.removeItemFromCart(lineItem)
      );
    } catch (e) {
      await this.catchServerError(e as Error);
    }
  };

  private readonly catchServerError = async (error: Error) => {
    this.clearLoadingItems();
    this.cartService.setHasErrorState(true);
    await this.controllerApi.updateComponent(false);
    this.sendErrorPresentedInCartSideCart(error.message);
    setTimeout(() => {
      void (async () => {
        await this.clearError();
      })();
    }, 5000);
  };

  private readonly applyCoupon = async (code: string) => {
    this.cartService.clearCouponError();
    await this.controllerApi.updateComponent();

    await this.controllerApi.reportFedops(FedopsInteractions.ApplyCoupon, () =>
      this.cartService.applyCoupon(code).catch(() => this.controllerApi.updateComponent())
    );
  };

  private readonly removeCoupon = () => {
    return this.controllerApi.reportFedops(FedopsInteractions.ApplyCoupon, () => this.cartService.removeCoupon());
  };

  private readonly sendToggleCouponBi = () => {
    return this.biService.clickOnApplyPromotionalCodeSf(this.cartService.cartModel);
  };

  private readonly sendAddNoteBi = () => {
    return this.biService.clickOnAddNoteToSellerSf(this.cartService.cartModel);
  };

  private readonly sendMinimumOrderMessageShownBi = (buttonEnabled: boolean) => {
    return this.biService.minimumOrderMessageIsShownInCart(
      this.cartService.cartModel,
      this.cartService.estimatedTotals,
      buttonEnabled
    );
  };

  private readonly sendErrorPresentedInCartSideCart = (errorMessage: string) => {
    return this.biService.errorPresentedInCartSideCart(errorMessage);
  };

  private readonly updateBuyerNote = (...args: Parameters<CartService['updateBuyerNote']>) => {
    return this.cartService.updateBuyerNote(...args);
  };

  private readonly getProductsManifest = () => {
    return this.productsService.manifest(this.cartService.cartModel);
  };

  private get isCartValid(): boolean {
    return this.cartService.areAllItemsInStock;
  }

  private get isPartiallyPaid(): boolean {
    return this.cartService.estimatedTotals?.payLater.total.convertedAmount > 0;
  }

  private get shouldDisplayViolations(): boolean {
    return this.cartService.estimatedTotals?.violations?.length > 0;
  }

  private readonly lineItemsLoaded = () => {
    this.flowAPI.fedops.interactionEnded(FedopsInteractions.lineItemsLoaded);
  };

  private readonly pageLoaded = () => {
    this.flowAPI.fedops.interactionEnded(FedopsInteractions.padeLoaded);
  };

  public setOverrides(veloInputs: VeloInputs) {
    this.settingsOverrides = {
      ...this.settingsOverrides,
      ...veloInputs,
    };
  }

  public async toProps(): Promise<CartStoreProps> {
    const experimentProps: ExperimentProps = {
      shouldPresentTooltipWithoutNumber: this.siteStore.experiments.enabled(SPECS.CartTooltipWithoutNumber),
      makeCartItemNameLink: this.siteStore.experiments.enabled(SPECS.MakeCartItemNameLink),
      shouldShowErrorMessage: this.siteStore.experiments.enabled(SPECS.ErrorMessage),
      AddSlotToCartPage: this.siteStore.experiments.enabled(SPECS.AddSlotToCartPage),
      useGetSettingsWithDefaults: this.siteStore.experiments.enabled(SPECS.UseGetSettingsWithDefaults),
      sideCartElementsVisibilityInCss: this.siteStore.experiments.enabled(SPECS.SideCartElementsVisibilityInCss),
      stopUseSelectedMembershipName: this.siteStore.experiments.enabled(SPECS.StopUseSelectedMembershipName),
      usePhotoMediaType: this.siteStore.experiments.enabled(SPECS.UsePhotoMediaType),
      showComparePriceWhenDifferent: this.siteStore.experiments.enabled(SPECS.ShowComparePriceWhenDifferent),
      useCouponCodeOnSummary: this.siteStore.experiments.enabled(SPECS.UseCouponCodeOnSummary),
      cartFromSDKWhenShowShippingOrShowTax: this.siteStore.experiments.enabled(
        SPECS.CartFromSDKWhenShowShippingOrShowTax
      ),
    };

    if (this.cartService.isEmpty) {
      return {
        shouldRenderEmptyState: true,
        lineItemsLoaded: this.lineItemsLoaded,
        pageLoaded: this.pageLoaded,
        ...experimentProps,
      } as unknown as CartStoreProps;
    }

    return {
      loadingItems: this.loadingItems,
      cartModel: this.cartService.cartModel,
      estimatedTotals: this.cartService.estimatedTotals,
      itemsCount: this.cartService.cartModel?.lineItems?.reduce((count, lineItem) => count + lineItem.quantity, 0),
      isCardTokenizationCart: this.cartService?.cartModel?.lineItems?.some((lineItem) => lineItem.savePaymentMethod),
      isNonShippableCart: this.cartService.isNonShippableCart,
      manifest: await this.getProductsManifest(),
      applyCoupon: this.applyCoupon,
      removeCoupon: this.removeCoupon,
      couponError: this.cartService.couponError,
      isFreeCart: this.cartService.isZeroCart,
      sendToggleCouponBi: this.sendToggleCouponBi,
      removeItemFromCart: this.removeItemFromCart,
      sendAddNoteBi: this.sendAddNoteBi,
      sendMinimumOrderMessageShownBi: this.sendMinimumOrderMessageShownBi,
      updateBuyerNote: this.updateBuyerNote,
      updateItemQuantity: this.updateItemQuantity,
      screenReaderMessage: this.screenReaderMessage,
      isCartValid: this.isCartValid,
      shouldShowCoupon: this.styleSettingsService.shouldShowCoupon,
      shouldShowBuyerNote: this.styleSettingsService.shouldShowBuyerNote,
      shouldShowExpressCheckout: this.styleSettingsService.isExpressCheckoutButtonShownInSomeBreakpoint,
      shouldShowMinimumOrderAmount: this.minimumOrderAmountService.shouldDisplayNotification,
      shouldShowTopCheckoutButtonInMobile: this.styleSettingsService.shouldShowTopCheckoutButtonInMobile,
      shouldDisplayViolations: this.shouldDisplayViolations,
      shouldShowSecureCheckout: this.settingsOverrides.shouldShowSecureCheckout,
      isPartiallyPaid: this.isPartiallyPaid,
      hasErrorViolations: this.cartService.hasErrorViolations,
      topThreeViolations: calcTopThreeViolations(
        this.cartService.estimatedTotals?.violations,
        this.siteStore.experiments.enabled(SPECS.SupportDeliveryViolationOnCart)
      ),
      ...experimentProps,
      shouldShowError: this.cartService.hasError,
      clearError: this.clearError,
      lineItemsLoaded: this.lineItemsLoaded,
      pageLoaded: this.pageLoaded,
      isLoadingSummary: this.cartService.isLoadingSummary,
    };
  }
}
